import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { 
  CheckCircle, 
  ChevronRight,
  Award,
  Users,
  Globe,
  Shield,
  Heart,
  Target
} from "lucide-react";
import { motion } from "framer-motion";

const OWNER_IMAGE = "/images/owner-closeup.jpg";

const values = [
  {
    icon: Shield,
    title: "Integrity First",
    description: "We uphold the highest ethical standards in every client interaction and tax preparation service."
  },
  {
    icon: Heart,
    title: "Client-Centered",
    description: "Your financial success is our priority. We tailor our services to meet your unique needs."
  },
  {
    icon: Award,
    title: "Professional Excellence",
    description: "Our team maintains the latest certifications and stays current with all tax law changes."
  },
  {
    icon: Target,
    title: "Results-Driven",
    description: "We focus on maximizing your refunds and minimizing your tax burden legally and ethically."
  }
];

const whyChoose = [
  "IRS-Compliant & Secure Filing",
  "Personalized One-on-One Service",
  "Year-Round Support (Not Seasonal)",
  "Transparent Pricing, No Hidden Fees",
  "Nationwide Service - All 50 States",
  "Multilingual Support (English, French, Creole)"
];

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-[68px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#121417] via-[#121417] to-[#152942] text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
                About <span className="text-[#C9A227]">WMJ Enterprise Services</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
                Professional tax preparation and financial services built on trust, expertise, and personalized care.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img 
                  src={OWNER_IMAGE} 
                  alt="WMJ Enterprise Services Team" 
                  className="rounded-lg shadow-2xl w-full"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#121417] mb-6">
                  Our Story
                </h2>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  <p>
                    WMJ Enterprise Services LLC was founded with a simple mission: to make professional tax preparation 
                    accessible, affordable, and stress-free for everyone—from individuals and families to small businesses 
                    and self-employed professionals.
                  </p>
                  <p>
                    We understand that taxes can be overwhelming. That's why we've built our practice around personalized 
                    service, transparent pricing, and year-round support. Unlike seasonal tax shops, we're here for you 
                    12 months a year, ready to answer questions, provide guidance, and help you stay on top of your 
                    financial obligations.
                  </p>
                  <p>
                    Our team serves clients nationwide across all 50 states, offering multilingual support in English, 
                    French, and Creole. We're proud to be trusted by military families, working professionals, freelancers, 
                    and small business owners who value integrity, expertise, and exceptional customer care.
                  </p>
                </div>

                <div className="mt-8 flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-[#C9A227]">1000+</div>
                    <div className="text-sm text-slate-600 mt-1">Clients Served</div>
                  </div>
                  <div className="h-12 w-px bg-slate-300"></div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-[#C9A227]">50</div>
                    <div className="text-sm text-slate-600 mt-1">States Covered</div>
                  </div>
                  <div className="h-12 w-px bg-slate-300"></div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-[#C9A227]">24/7</div>
                    <div className="text-sm text-slate-600 mt-1">Client Portal Access</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#121417] mb-4">
                Our Core Values
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                The principles that guide everything we do and every client relationship we build.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-14 h-14 bg-[#C9A227]/10 rounded-lg flex items-center justify-center mb-4">
                    <value.icon className="text-[#C9A227] w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-[#121417] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#121417] mb-4">
                Why Choose <span className="text-[#C9A227]">WMJ Enterprise Services</span>
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                We combine professional expertise with personalized service to deliver exceptional results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChoose.map((reason, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-start gap-3 p-6 bg-slate-50 rounded-lg"
                >
                  <CheckCircle className="text-[#C9A227] w-6 h-6 flex-shrink-0 mt-1" />
                  <span className="text-[#121417] font-medium">{reason}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas Section */}
        <section className="py-20 bg-gradient-to-br from-[#121417] to-[#152942] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Globe className="w-16 h-16 text-[#C9A227] mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                Serving Clients <span className="text-[#C9A227]">Nationwide</span>
              </h2>
              <p className="text-lg text-slate-200 max-w-3xl mx-auto mb-8 leading-relaxed">
                No matter where you are in the United States, WMJ Enterprise Services is here to help. 
                We serve clients in all 50 states with the same level of professionalism, care, and expertise.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <Users className="inline mr-2 w-5 h-5" />
                  <span className="font-semibold">Individuals & Families</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <Award className="inline mr-2 w-5 h-5" />
                  <span className="font-semibold">Small Businesses</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <Target className="inline mr-2 w-5 h-5" />
                  <span className="font-semibold">Self-Employed</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#121417] mb-6">
                Ready to Experience the WMJ Difference?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Join hundreds of satisfied clients who trust us with their tax preparation and financial services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/intake">
                  <Button 
                    size="lg" 
                    className="bg-[#C9A227] hover:bg-[#B89120] text-[#121417] font-bold text-lg px-8 py-6"
                  >
                    Start Your Tax Filing
                    <ChevronRight className="ml-2" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-[#121417] text-[#121417] hover:bg-[#121417] hover:text-white font-semibold text-lg px-8 py-6"
                  >
                    View Our Services
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
